import { Query, QueryFunctionContext, UseQueryResult } from '@tanstack/react-query';
import {
  compact,
  difference,
  filter,
  find,
  fromPairs,
  intersection,
  isEmpty,
  isEqual,
  isNumber,
  join,
  keyBy,
  map,
  maxBy,
  size,
  uniq,
} from 'es-toolkit/compat';
import { byteLengthFromUrl, FileMetaData } from 'hyparquet';
import { useMemo } from 'react';

import { isNotPresignedUrl } from './api';
import { getParquetResourceId, measureParquetStage } from './parquetWorker/instrumentation';
import {
  asyncBufferFromUrlWithGetFallback,
  byteLengthFromUrlWithGet,
  cachedAsyncBufferWithFixedWindows,
} from './parquetWorker/rangeBuffer';
import { getParquetColumnGroupKey, ParquetFile, ParquetRow } from './parquetWorker/types';
import { parquetWorkerClient } from './parquetWorker/workerClient';
import queryClient, { stabilizedQueriesDependency } from './queryClient';
import { useQueriesWithErrorAndRetrySnackbar } from './useQueryWithErrorAndRetrySnackbar';

export { asyncBufferFromUrlWithGetFallback, byteLengthFromUrlWithGet, cachedAsyncBufferWithFixedWindows };
export type { ParquetFile, ParquetRow };

const INDEX_OF_COLUMNS_IN_QUERY_KEY = 3;

export interface ParquetUrlAndColumns {
  urlForGetRequest: string;
  urlForHeadRequest?: string;
  // Certain heatmaps visualize data from multiple columns, so we will store their state according to this grouping
  columnsGroupForHeatmap?: string[];
  // Of the heatmap group, which columns enabled by the user
  columnsForQuery?: string[];
  optionalColumnsForQuery?: string[];
  // Is the query enabled by the user
  enabled: boolean;
  // Warm lightweight parquet metadata for faster activation without holding row arrays in memory
  prefetch?: boolean;
  additionalRowContext?: Record<string, any>;
  // In-memory result shape. Parquet remains columnar on disk in either mode;
  // use `columnar` for typed-array/GPU consumers and `rows` for record-oriented consumers.
  outputFormat?: 'rows' | 'columnar';
}
export interface ParquetFileWithQueryContext extends ParquetFile, Partial<ParquetUrlAndColumns> {
  isLoading: boolean;
  dataUpdatedAt: number;
}

const canQueryParquet = (url: string) => Boolean(url) && !isNotPresignedUrl(url);

const getRequestedColumns = ({
  columnsForQuery,
  columnsGroupForHeatmap,
}: Pick<ParquetUrlAndColumns, 'columnsForQuery' | 'columnsGroupForHeatmap'>): string[] | undefined =>
  columnsForQuery || columnsGroupForHeatmap;

const getParquetResultKey = ({
  urlForGetRequest,
  urlForHeadRequest,
  columnsForQuery,
  columnsGroupForHeatmap,
  outputFormat,
  optionalColumnsForQuery,
}: ParquetUrlAndColumns): string =>
  JSON.stringify([
    'parquet',
    urlForGetRequest,
    urlForHeadRequest,
    getRequestedColumns({ columnsForQuery, columnsGroupForHeatmap }),
    outputFormat,
    optionalColumnsForQuery,
  ]);

const getParquetWarmCacheKey = ({
  urlForGetRequest,
  urlForHeadRequest,
}: Pick<ParquetUrlAndColumns, 'urlForGetRequest' | 'urlForHeadRequest'>): string =>
  `${urlForGetRequest}::${urlForHeadRequest || ''}`;

export const useParquetFileQueries = (parquetQueryInputs: ParquetUrlAndColumns[], debug: boolean = false) => {
  const parquetFilesToWarm = compact(
    Object.values(
      keyBy(
        map(
          filter(parquetQueryInputs, ({ enabled, prefetch }) => enabled || prefetch),
          ({ urlForGetRequest, urlForHeadRequest }) => ({
            cacheKey: getParquetWarmCacheKey({ urlForGetRequest, urlForHeadRequest }),
            urlForHeadRequest,
            urlForGetRequest,
          })
        ),
        'cacheKey'
      )
    )
  );
  const activeParquetQueryInputs = filter(
    parquetQueryInputs,
    ({ urlForGetRequest, enabled }) => enabled && canQueryParquet(urlForGetRequest)
  );
  const byteLengthQueries = useQueriesWithErrorAndRetrySnackbar<number>({
    queries: map(parquetFilesToWarm, ({ urlForGetRequest, urlForHeadRequest }) => ({
      queryKey: ['parquetByteLength', urlForGetRequest, urlForHeadRequest],
      queryFn: () =>
        urlForHeadRequest ? byteLengthFromUrl(urlForHeadRequest) : byteLengthFromUrlWithGet(urlForGetRequest),
      enabled: canQueryParquet(urlForGetRequest),
      retry: false,
      queriedEntityName: 'parquet file byte length',
      consoleOnly: true,
    })),
  });
  const byteLengthQueriesByWarmCacheKey = keyBy(
    map(parquetFilesToWarm, ({ cacheKey }, index) => ({ cacheKey, query: byteLengthQueries[index] })),
    'cacheKey'
  );
  const metadataQueries = useQueriesWithErrorAndRetrySnackbar<Pick<FileMetaData, 'schema' | 'num_rows'>>({
    queries: map(parquetFilesToWarm, ({ urlForGetRequest, urlForHeadRequest }, index) => {
      const byteLengthQuery = byteLengthQueries[index];

      return {
        queryKey: ['parquetMetadata', urlForGetRequest, urlForHeadRequest],
        queryFn: ({ signal }: QueryFunctionContext) =>
          parquetWorkerClient.readMetadata(
            {
              parquetFileUrl: urlForGetRequest,
              byteLength: byteLengthQuery?.data,
            },
            signal
          ),
        enabled: canQueryParquet(urlForGetRequest) && isNumber(byteLengthQuery?.data),
        retry: false,
        queriedEntityName: 'parquet file metadata',
        consoleOnly: true,
      };
    }),
  });
  const metadataQueriesByWarmCacheKey = keyBy(
    map(parquetFilesToWarm, ({ cacheKey }, index) => ({ cacheKey, query: metadataQueries[index] })),
    'cacheKey'
  );
  const queries: UseQueryResult<ParquetFile, any>[] = useQueriesWithErrorAndRetrySnackbar({
    snackbarOptions: { persist: true, preventDuplicate: true, variant: 'error' },
    queries: map(
      activeParquetQueryInputs,
      ({
        urlForGetRequest,
        urlForHeadRequest,
        columnsForQuery,
        columnsGroupForHeatmap,
        additionalRowContext,
        outputFormat,
        optionalColumnsForQuery,
      }) => {
        // If no columns are specified, use the columns group for heatmap
        columnsForQuery = columnsForQuery || columnsGroupForHeatmap;
        const byteLengthQuery =
          byteLengthQueriesByWarmCacheKey[getParquetWarmCacheKey({ urlForGetRequest, urlForHeadRequest })]?.query;
        const metadataQuery =
          metadataQueriesByWarmCacheKey[getParquetWarmCacheKey({ urlForGetRequest, urlForHeadRequest })]?.query;
        if (!isEmpty(optionalColumnsForQuery) && metadataQuery?.data) {
          columnsForQuery = uniq([
            ...(columnsForQuery || []),
            ...intersection(optionalColumnsForQuery, map(metadataQuery.data.schema, 'name')),
          ]);
        }
        const queryKey = ['parquet', urlForGetRequest, urlForHeadRequest, columnsForQuery, outputFormat];
        const cachedQueryResultsForUrl = filter(
          queryClient.getQueryCache().findAll(['parquet', urlForGetRequest]),
          (query) =>
            !isEqual(query.queryKey, queryKey) && query.queryKey[4] === outputFormat && Boolean(query.state.data)
        ) as Query<ParquetFile, any>[];
        // If a previous query already contained the columns we are querying, use that query
        // to avoid re-querying the same columns
        const queryContainingColumns = find(cachedQueryResultsForUrl, (query) => {
          const queryColumns = query.queryKey?.[INDEX_OF_COLUMNS_IN_QUERY_KEY] as string[] | undefined;
          return isEqual(intersection(queryColumns, columnsForQuery), columnsForQuery);
        });

        const queryResultsWithMaxIntersectionSize = maxBy(cachedQueryResultsForUrl, (query) =>
          size(intersection(query.queryKey[INDEX_OF_COLUMNS_IN_QUERY_KEY] as string[], columnsForQuery))
        );

        return {
          queriedEntityName: `parquet file with columns ${join(columnsForQuery, ', ')}`,
          initialData: queryContainingColumns?.state.data,
          placeholderData: queryResultsWithMaxIntersectionSize?.state.data,
          queryKey,
          queryFn: async ({ signal }: QueryFunctionContext): Promise<ParquetFile> => {
            if (!isEmpty(optionalColumnsForQuery) && metadataQuery?.isError) {
              throw metadataQuery.error;
            }
            const existingData = queryResultsWithMaxIntersectionSize?.state.data;
            if (!existingData) {
              if (debug) {
                console.debug(
                  `No existing data for parquet file ${urlForGetRequest} with columns ${join(
                    columnsForQuery,
                    ', '
                  )}, querying all columns`,
                  {
                    urlForGetRequest,
                    columnsForQuery,
                    additionalRowContext,
                    byteLength: byteLengthQuery?.data,
                  }
                );
              }
              // If no existing data, read all columns
              return parquetWorkerClient.readRows(
                {
                  parquetFileUrl: urlForGetRequest,
                  columnsForQuery,
                  byteLength: byteLengthQuery?.data,
                  additionalRowContext,
                  outputFormat,
                },
                signal
              );
            }
            // Only query for columns that are not already in the existing data
            const existingColumns = existingData.columnsQueried;
            const newColumnsToQuery = difference(columnsForQuery, existingColumns);
            if (isEmpty(newColumnsToQuery)) {
              if (debug) {
                console.debug(
                  `No new columns to query for parquet file ${urlForGetRequest}, returning existing data`,
                  existingData
                );
              }
              return existingData; // No new columns to query, return existing data
            }
            if (debug) {
              console.debug(
                `Querying new columns for parquet file ${urlForGetRequest}: ${join(newColumnsToQuery, ', ')}`,
                {
                  urlForGetRequest,
                  newColumnsToQuery,
                  existingColumns,
                  additionalRowContext,
                  byteLength: byteLengthQuery?.data,
                }
              );
            }
            // If there are new columns to query, read only those columns
            // and merge the results with the existing data
            // This is to avoid re-querying the same columns and to optimize performance
            // Decode missing columns and assemble full rows behind the worker boundary.
            if (debug) {
              console.debug(
                `Merging results for parquet file ${urlForGetRequest} with new columns ${join(
                  newColumnsToQuery,
                  ', '
                )}`,
                {
                  existingColumns,
                  existingData,
                }
              );
            }
            const mergedResults = await parquetWorkerClient.readRows(
              {
                parquetFileUrl: urlForGetRequest,
                columnsForQuery: newColumnsToQuery,
                byteLength: byteLengthQuery?.data,
                baseFile: existingData,
                outputFormat,
              },
              signal
            );
            if (debug) {
              console.debug(
                `Merged results for parquet file ${urlForGetRequest} with new columns ${join(newColumnsToQuery, ', ')}`,
                mergedResults
              );
            }
            return mergedResults;
          },
          enabled:
            isNumber(byteLengthQuery?.data) &&
            (isEmpty(optionalColumnsForQuery) || Boolean(metadataQuery?.data) || metadataQuery?.isError),
          cacheTime: 0,
          retry: false,
        };
      }
    ),
  });

  const activeQueriesByKey = keyBy(
    map(activeParquetQueryInputs, (parquetQueryInput, index) => ({
      resultKey: getParquetResultKey(parquetQueryInput),
      query: queries[index],
    })),
    'resultKey'
  );

  return useMemo(
    () =>
      map(
        parquetQueryInputs,
        (parquetQueryInput) =>
          activeQueriesByKey[getParquetResultKey(parquetQueryInput)]?.query ||
          ({ data: undefined, dataUpdatedAt: 0, isLoading: false } as UseQueryResult<ParquetFile, any>)
      ),
    [parquetQueryInputs, activeQueriesByKey]
  );
};

/**
 * Fetch multiple parquet files and columns in parallel, and return the results as a hierarchical dictionary of parquet files by URL and
 * then by the column groups they belong to (regardless of the specific columns requested).
 *
 * @param {ParquetUrlAndColumns[]} parquetQueryInputs
 * @returns {{ parquetFiles: { [url: string]: { [columnsGroupForHeatmap: join(columnsGroupForHeatmap, ',')]: {
 *  rows: ParquetRow[];
 *  metadata: FileMetaData;
 *  isLoading: boolean;
 *  urlForGetRequest: string;
 *  urlForHeadRequest: string;
 *  columnsGroupForHeatmap: string[];
 *  columnsForQuery: string[];
 * } } } }}
 */
export const useParquetFilesByColumnGroup = (parquetQueryInputs: ParquetUrlAndColumns[]) => {
  const parquetFileQueries = useParquetFileQueries(parquetQueryInputs);
  const stabilizedParquetQueryInputsDependency = JSON.stringify(parquetQueryInputs);

  const parquetFiles: Record<string, Record<string, ParquetFileWithQueryContext>> = useMemo(() => {
    const parsedResults = map(parquetFileQueries, (query, index) => {
      const matchingQuery = parquetQueryInputs[index];
      const rows = query?.data?.rows || [];
      return {
        columnsQueried: query?.data?.columnsQueried,
        rows,
        columnarData: query?.data?.columnarData,
        metadata: query?.data?.metadata,
        isLoading: query?.isFetching && canQueryParquet(matchingQuery?.urlForGetRequest),
        dataUpdatedAt: query?.dataUpdatedAt,
        ...(matchingQuery || {}),
      };
    });

    const parquetUrls = compact(uniq(map(parquetQueryInputs, 'urlForGetRequest')));

    return fromPairs(
      map(parquetUrls, (urlForGetRequest) => {
        const resultsWithUrl = filter(parsedResults, (result) => result.urlForGetRequest === urlForGetRequest);
        // We key by all the group's columns, even if we query only a subset of them
        return [
          urlForGetRequest,
          measureParquetStage(
            {
              resourceId: getParquetResourceId(urlForGetRequest),
              stage: 'downstream-consumption',
              thread: 'main',
              rowCount: resultsWithUrl.reduce(
                (sum, result) => sum + (result.columnarData?.length ?? result.rows.length),
                0
              ),
              columnCount: resultsWithUrl.reduce((sum, result) => sum + size(result.columnsQueried ?? []), 0),
            },
            () =>
              keyBy(resultsWithUrl, ({ columnsGroupForHeatmap, outputFormat }) =>
                getParquetColumnGroupKey(columnsGroupForHeatmap, outputFormat)
              )
          ),
        ];
      })
    );
  }, [stabilizedQueriesDependency(parquetFileQueries), stabilizedParquetQueryInputsDependency]);

  return { parquetFiles, parquetFileQueries, parquetQueryInputs };
};
